import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { generatePath, useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import { Button, Modal, Switch, Tooltip, notification } from 'antd'
import * as Icons from '@ant-design/icons'
import produce from 'immer'
import _ from 'lodash'

import { EditLink } from 'admin/lib/Common/StyledElements'
import {
  AddNewItem,
  CreationDate,
  Form,
  FormSectionTitle,
  FormSectionWrapper,
  SectionProvider
} from 'admin/lib/Forms'
import { HandleSingleAndDoubleClick } from 'admin/lib/Functions/handleSingleAndDoubleClick'
import {
  getLocaleFromCountry,
  isValidTimeZone
} from 'admin/lib/Functions/timeZoneHelper'
import { Flag } from 'admin/lib/Icons/Flag'
import { useGlobalShortcut } from 'admin/lib/shortcuts'
import { Matchers } from 'admin/lib/Records'
import * as ROUTES from 'admin/routes'

import { WasteLineItem } from './WasteLineItem'
import { WasteData } from './WasteData'
import { useLineItemStore } from 'admin/pages/RecordEdit/LineItemStore'
import {
  AddMultipleLineItemsModal,
  DuplicateRecordControls,
  FormControls,
  LastModifiedBy,
  RecordHistory,
  focusFirstError,
  useLineItemsManagement,
  AttachmentView,
  useExtractionValueClicked,
  TemplateNameLabel,
  AutoPublishFailureReason
} from 'admin/pages/RecordEdit/common'
import {
  isoDateFromFormDate,
  isoDateToFormDate,
  cleanDate
} from 'admin/lib/dates'
import {
  RAW,
  ENRICHED,
  ENRICHED_DISABLED_MESSAGE
} from 'admin/pages/RecordEdit/common/constants'
import { LineItemSummary } from 'admin/pages/RecordEdit/common/LineItemSummary'
import { PossibleDuplicateRecordControls } from 'admin/pages/RecordEdit/common/PossibleDuplicateRecordControls'
import {
  LineItemWrapper,
  TitleBar,
  ShowRawWrapper,
  DataToggle,
  DataToggleItem
} from 'admin/pages/RecordEdit/common/styledComponents'

const DEFAULT_ITEM = Object.freeze({
  id: null,
  containerType: '',
  knownContainerType: '',
  wasteCode: '',
  volumeEstimated: '',
  volume: '',
  volumeUoM: 'CubicMeter',
  weightEstimated: '',
  weight: '',
  weightUoM: 'Tonne',
  quantity: 1
})

const DEFAULT_ITEM_IMPERIAL = Object.freeze({
  ...DEFAULT_ITEM,
  volumeUoM: 'CubicYard',
  weightUoM: 'Pound'
})

const DEFAULT_RAW_ITEM = Object.freeze({
  id: null,
  containerType: '',
  wasteCode: '',
  volume: '',
  weight: '',
  quantity: 1
})

export function Waste({
  record,
  submit,
  project,
  isImperial,
  showHistory,
  onHistoryCloseClick,
  saving,
  acceptDataAfterSaveCount
}) {
  const lastAcceptedSaveCountRef = useRef(-1)
  const [reloadItemsCount, setReloadItemsCount] = useState(0)
  const [showDebug, setShowDebug] = useState(false)
  const [lineItemSummaryOpen, setLineItemSummaryOpen] = useState(false)
  const [isAddMultipleLineItemsOpen, setIsAddMultipleLineItemsOpen] =
    useState(false)

  const legacyRecord = record && !record?.qcrAppliedToRaw
  const [dataView, setDataView] = useState(!legacyRecord ? RAW : ENRICHED)

  if (sessionStorage.getItem('last-data-view')) {
    setDataView(sessionStorage.getItem('last-data-view'))
    sessionStorage.removeItem('last-data-view')
  }

  const [showRaw, setShowRaw] = useState(false)
  const [enrichedDisabled, setEnrichedDisabled] = useState(false)

  const form = useFormContext()
  const { moduleName, projectId, recordId, accountId } = useParams()
  const { reset, setValue } = form

  const [lineItemErrors, setLineItemErrors] = useState({})

  const projectCountry = project.country
  const projectTimeZone = project.timeZone ?? 'Europe/London'
  const projectLocale = getLocaleFromCountry(projectCountry)

  const MULTIATTACHMENT_WARNING_MESSAGE =
    'Multiple images uploaded with this record'

  const enrichedSourceTracking = useMemo(
    () => record?.enriched?.sourceTracking,
    [record]
  )

  const {
    methods,
    rawMethods,
    lineItemDuplicateHandlers,
    lineItemRemoveHandlers,
    handleAppendLineItem,
    handleAppendLineItemNoDebounce
  } = useLineItemsManagement(
    form,
    'enriched.items',
    'raw.items',
    isImperial ? DEFAULT_ITEM_IMPERIAL : DEFAULT_ITEM,
    DEFAULT_RAW_ITEM
  )

  const focusOnNewItem = () => {
    // focus on new element when ready
    const waitForInterval = setInterval(() => {
      const element = document.querySelector(
        `textarea[name^="uniquePrefix[${methods.fields.length}].raw_containerType"`
      )
      if (element !== null) {
        clearInterval(waitForInterval)
        element.focus()
        const form = document.getElementsByTagName('form')[0]
        form.scrollTo({
          top: form.scrollHeight,
          behavior: 'smooth'
        })
      }
    }, 500)
  }

  const focusOnFirstNewItem = numOfNewItems => {
    // focus on first new element when ready
    const waitForInterval = setInterval(() => {
      const element = document.querySelector(
        `textarea[name^="uniquePrefix[${methods.fields.length}].raw_containerType"`
      )
      if (element !== null) {
        clearInterval(waitForInterval)
        element.closest('details').scrollIntoView({ behavior: 'smooth' })
        setTimeout(() => element.focus(), 500)
      }
    }, 500)
  }

  const openAddNewItemsModal = e => {
    setIsAddMultipleLineItemsOpen(true)
  }

  const closeAddNewItemsModal = e => {
    setIsAddMultipleLineItemsOpen(false)
  }

  const addNewLineItem = e => {
    handleAppendLineItem()
    focusOnNewItem()
  }

  const addMultipleNewLineItems = numOfNewItems => {
    if (numOfNewItems > 0) {
      for (let i = 1; i <= numOfNewItems; i++) {
        handleAppendLineItemNoDebounce()
      }
      focusOnFirstNewItem(numOfNewItems)
    }
  }

  const handleAddNewLineItem = HandleSingleAndDoubleClick(
    addNewLineItem,
    openAddNewItemsModal
  )

  const duplicateLineItem = item => {
    lineItemDuplicateHandlers(item)
    focusOnNewItem()
  }

  const duplicateLineItemMultiple = (numOfNewItems, originalItem) => {
    if (numOfNewItems > 0) {
      for (let i = 1; i <= numOfNewItems; i++) {
        lineItemDuplicateHandlers(originalItem)
      }
      focusOnFirstNewItem(numOfNewItems)
    }
  }

  const extractionValueClicked = useExtractionValueClicked(setValue)

  useEffect(() => {
    // Reset form if:
    // 1. Never saved (acceptDataAfterSaveCount === -1), OR
    // 2. Saved and this is fresh data we haven't processed yet (acceptDataAfterSaveCount > lastAcceptedSaveCountRef)
    // AND not currently saving
    if (
      !saving &&
      (acceptDataAfterSaveCount === -1 ||
        acceptDataAfterSaveCount > lastAcceptedSaveCountRef.current)
    ) {
      lastAcceptedSaveCountRef.current = acceptDataAfterSaveCount
      const formData = produce(record, draft => {
        draft.enriched.removalDate = cleanDate(draft.enriched.removalDate)
        draft.enriched.removalDate = isoDateToFormDate(
          draft.enriched.removalDate,
          projectTimeZone
        )
        draft.enriched.removalExitDate = cleanDate(
          draft.enriched.removalExitDate
        )
        draft.enriched.removalExitDate = isoDateToFormDate(
          draft.enriched.removalExitDate,
          projectTimeZone
        )
      })
      reset(formData)

      // Manually sync line items to store to avoid timing issues with unsavedChanges flag
      if (formData.enriched?.items && formData.raw?.items) {
        formData.enriched.items.forEach((item, index) => {
          const rawItem = formData.raw.items[index] || {}
          const rawAndEnhancedItem = { ...item }
          Object.entries(rawItem).forEach(([key, value]) => {
            rawAndEnhancedItem[`raw_${key}`] = value
          })
          useLineItemStore.setState({ [index]: rawAndEnhancedItem })
        })
      }
    }
  }, [projectTimeZone, record, reset, saving, acceptDataAfterSaveCount])

  const toggleDebug = e => {
    if (e.detail === 3) {
      setShowDebug(s => !s)
    }
  }

  const handleToggleDataView = () => {
    if (dataView === RAW) {
      if (!enrichedDisabled) {
        performance.mark('raw-enriched-tab-switch', {
          detail: { context: 'shortcut', destination: ENRICHED }
        })
        setDataView(ENRICHED)
      } else {
        notification.warning({
          message: ENRICHED_DISABLED_MESSAGE,
          placement: 'topLeft'
        })
      }
    } else {
      performance.mark('raw-enriched-tab-switch', {
        detail: { context: 'shortcut', destination: RAW }
      })
      setDataView(RAW)
    }
  }

  const handlToggleShowRaw = () => {
    setShowRaw(prev => !prev)
  }

  const handleSetLineItemErrors = (errors, index) => {
    const updatedErrorArray =
      lineItemErrors && lineItemErrors.uniquePrefix
        ? [...lineItemErrors.uniquePrefix]
        : []

    // Replace the error message for the line item at the given index
    if (errors?.uniquePrefix && errors.uniquePrefix[index]) {
      updatedErrorArray[index] = errors.uniquePrefix[index]
    } else {
      updatedErrorArray[index] = undefined
    }

    if (
      updatedErrorArray &&
      updatedErrorArray.filter(item => typeof item !== 'undefined').length > 0
    ) {
      setLineItemErrors({ uniquePrefix: updatedErrorArray })
    } else {
      setLineItemErrors({})
    }
  }

  const handleSubmit = form.handleSubmit(
    formRecord => {
      if (Object.keys(lineItemErrors).length > 0) {
        focusFirstError(form.errors, lineItemErrors)
        return
      }

      submit(
        trackAndProduceRecord(record, formRecord, projectTimeZone),
        dataView
      )
    },
    () => focusFirstError(form.errors, lineItemErrors)
  )

  const submitAddressCheck = event => {
    event.preventDefault()

    // Prevent double submission
    if (saving) {
      return
    }

    const formValues = form.getValues()
    if (
      dataView === ENRICHED &&
      (formValues.status === 'Published' ||
        formValues.status === 'AutoPublished') &&
      ((formValues.enriched.originSiteAddress &&
        !formValues.enriched.originSiteAddressKnown) ||
        (formValues.enriched.facilitySiteAddress &&
          !formValues.enriched.facilitySiteAddressKnown))
    ) {
      Modal.confirm({
        title: '1 or more Known Addresses missing.',
        content: (
          <>
            <p>This record will be published without mileage calculation.</p>
            <p> Are you sure you want to publish this record?</p>
          </>
        ),
        okText: 'Save enriched',
        onOk: handleSubmit
      })
    } else {
      handleSubmit()
    }
  }

  const openAllItems = useCallback(() => {
    document.querySelectorAll('.LineItem').forEach((item, index) => {
      sessionStorage.setItem(`${recordId}-${index}-open`, true)
    })
    setReloadItemsCount(prev => prev + 1)
  }, [recordId])

  const closeAllItems = useCallback(() => {
    document.querySelectorAll('.LineItem').forEach((item, index) => {
      sessionStorage.setItem(`${recordId}-${index}-open`, false)
    })
    setReloadItemsCount(prev => prev + 1)
  }, [recordId])

  useGlobalShortcut(Matchers.form.toggleDataView, handleToggleDataView)
  useGlobalShortcut(Matchers.form.toggleShowRaw, handlToggleShowRaw)
  useGlobalShortcut(Matchers.lineItems.openLineItemSummary, () =>
    setLineItemSummaryOpen(true)
  )
  useGlobalShortcut(Matchers.lineItems.minimizeProduct, () => {
    const parentDetails = document.activeElement?.closest(
      'details.LineItemDetails'
    )
    if (parentDetails) {
      parentDetails.querySelector('.productCollapseIcon')?.click()

      // Move focus to first element of next open <details>
      // This is to ensure that focus is moved to the next open product
      let element = parentDetails.parentElement

      while (element) {
        element = element.nextElementSibling
        const nextDetails = element?.querySelector('details[open]')
        if (nextDetails) {
          const firstInput = nextDetails.querySelector('textarea')
          if (firstInput) {
            firstInput.scrollIntoView({ behavior: 'smooth', block: 'center' })
            firstInput.focus()
            break
          }
        }
      }
    }
  })

  const currentStatus = form.watch('status')

  // Enriched confidence and validation results for the record, used to show warnings based on the confidence level
  const enrichedConfidenceAndValidation = useMemo(
    () => record?.enrichedConfidenceAndValidation,
    [record?.enrichedConfidenceAndValidation]
  )

  return (
    <SectionProvider style={{ gap: '15px' }}>
      <Form id="form-panel" onSubmit={submitAddressCheck}>
        {record.recordSimilarityDetails && (
          <DuplicateRecordControls
            recordSimilarityDetails={record.recordSimilarityDetails}
            recordStatus={record.status}
          />
        )}
        {record.duplicateRecord?.possibleDuplicateRecordIds?.length > 0 && (
          <PossibleDuplicateRecordControls details={record.duplicateRecord} />
        )}
        <TemplateNameLabel
          templateName={record.templateName}
          estimatedAccuracy={record.estimatedAccuracy}
          matchedRecord={record.matchedRecord}
          moduleName={moduleName}
        />
        <AutoPublishFailureReason
          autoPublishFailureReasons={record.autoPublishFailureReasons}
        />

        {record.attachmentPages > 1 && (
          <p className="warning">{MULTIATTACHMENT_WARNING_MESSAGE}</p>
        )}
        <FormControls dataView={dataView} saving={saving} />
        <LastModifiedBy
          modifier={record.modifier}
          modifiedById={record.modifiedBy}
        />
        {record.migrationSource !== null && (
          <p>
            Migrated from recordId:{' '}
            <EditLink
              key={accountId}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontWeight: 'normal' }}
              href={generatePath(ROUTES.RECORD.EDIT, {
                accountId: accountId,
                projectId: record.migrationSource.projectId,
                moduleName: record.migrationSource.moduleTagDefinitionName,
                recordId: record.migrationSource.recordId
              })}
            >
              {record.migrationSource.recordId}
            </EditLink>
            <br />
            Migration Reason: {record.migrationSource.migrationReason}
          </p>
        )}
        {legacyRecord && (
          <p className="warning">
            This record was created before we separated Raw and Enriched data.
            So expect the RAW values to be blank.
          </p>
        )}
        <CreationDate style={{ margin: '0 0 8px' }}>
          <>
            <div style={{ fontWeight: '600' }} onClick={toggleDebug}>
              Creation Date
            </div>
            <div style={{ lineHeight: '1.2', margin: '4px 0' }}>
              {`${
                projectTimeZone &&
                isValidTimeZone(projectTimeZone) &&
                Intl.DateTimeFormat().resolvedOptions().timeZone !==
                  projectTimeZone
                  ? 'Local: '
                  : ''
              }${new Date(record.createdAt).toLocaleString([], {
                timeZoneName: 'short',
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric'
              })}`}
              {
                // If Project Locale differs from local Locale
                // Or project TimeZone differs from local TimeZone
                (projectLocale !==
                  Intl.DateTimeFormat().resolvedOptions().locale ||
                  (projectTimeZone &&
                    isValidTimeZone(projectTimeZone) &&
                    Intl.DateTimeFormat().resolvedOptions().timeZone !==
                      projectTimeZone)) && (
                  <>
                    <br />
                    Project:{' '}
                    {new Date(record.createdAt).toLocaleString(projectLocale, {
                      timeZone: projectTimeZone,
                      timeZoneName: 'short',
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: 'numeric',
                      minute: 'numeric'
                    })}
                  </>
                )
              }
            </div>
          </>
        </CreationDate>
        {showDebug && (
          <div
            style={{ marginBottom: '5px', fontSize: 'smaller' }}
            tabindex="-1"
          >
            <dl>
              <dt>Journey distance (mi)</dt>
              <dd>{record.enriched.journey?.distanceMiles}</dd>
            </dl>
            <dl>
              <dt>
                Laden journey emissions (kg CO<sub>2</sub>e)
              </dt>
              <dd>{record.enriched.journey?.ladenEmissionsKgCO2e}</dd>
            </dl>
            <dl>
              <dt>
                Unladen journey emissions (kg CO<sub>2</sub>e)
              </dt>
              <dd>{record.enriched.journey?.unladenEmissionsKgCO2e}</dd>
            </dl>
          </div>
        )}

        <WasteData
          createdAt={record.createdAt}
          projectName={project.name}
          projectCountry={projectCountry}
          projectTimeZone={projectTimeZone}
          projectId={projectId}
          isImperial={isImperial}
          accountId={accountId}
          dataView={dataView}
          showRaw={showRaw}
          enrichedDisabled={enrichedDisabled}
          setDataView={value => {
            setDataView(value)
          }}
          setShowRaw={value => {
            setShowRaw(value)
          }}
          setEnrichedDisabled={value => {
            setEnrichedDisabled(value)
          }}
          enrichedConfidenceAndValidation={enrichedConfidenceAndValidation}
          enrichedSourceTracking={enrichedSourceTracking}
          attachmentFileNames={record.attachments?.map(att => att.name) || []}
        />

        <LineItemWrapper className="LineItemWrapper">
          <FormSectionWrapper
            style={{
              flexDirection: 'column',
              width: '100%',
              alignItems: 'flex-end'
            }}
          >
            <TitleBar>
              <FormSectionTitle level={5}>
                <Flag country={projectCountry} /> Items (
                {
                  methods.fields.filter(
                    item => !item.isDeleted && !item.isRemoved
                  ).length
                }
                )
              </FormSectionTitle>
              {dataView === ENRICHED && (
                <ShowRawWrapper>
                  <Switch
                    checked={showRaw}
                    onChange={() => setShowRaw(prev => !prev)}
                    size="small"
                  />
                  <div style={{ marginTop: '1px' }}>Show Raw</div>
                </ShowRawWrapper>
              )}
              <DataToggle>
                <DataToggleItem
                  className={dataView === RAW ? 'active' : ''}
                  onClick={() => {
                    performance.mark('raw-enriched-tab-switch', {
                      detail: { context: 'items', destination: RAW }
                    })
                    setDataView(RAW)
                  }}
                >
                  Raw
                </DataToggleItem>
                <DataToggleItem
                  className={
                    dataView === ENRICHED
                      ? 'active'
                      : enrichedDisabled
                        ? 'disabled'
                        : ''
                  }
                  onClick={() => {
                    if (!enrichedDisabled) {
                      performance.mark('raw-enriched-tab-switch', {
                        detail: { context: 'items', destination: ENRICHED }
                      })
                      setDataView(ENRICHED)
                    }
                  }}
                >
                  <Tooltip
                    title={enrichedDisabled ? ENRICHED_DISABLED_MESSAGE : ''}
                    placement="right"
                    mouseLeaveDelay={0.5}
                  >
                    Enriched
                  </Tooltip>
                </DataToggleItem>
              </DataToggle>
            </TitleBar>

            <TitleBar style={{ alignItems: 'baseline', margin: '0.25rem 0' }}>
              <div>
                <Tooltip title="Open all items">
                  <Icons.EyeOutlined
                    style={{ fontSize: '1.25rem', marginRight: '0.5rem' }}
                    onClick={openAllItems}
                  />
                </Tooltip>
                <Tooltip title="Close all items">
                  <Icons.EyeInvisibleOutlined
                    style={{ fontSize: '1.25rem' }}
                    onClick={closeAllItems}
                  />
                </Tooltip>
              </div>
              <Button onClick={() => setLineItemSummaryOpen(true)}>
                View Summary
              </Button>
              {lineItemSummaryOpen && methods.fields && rawMethods.fields && (
                <LineItemSummary
                  isOpen={lineItemSummaryOpen}
                  onClose={() => setLineItemSummaryOpen(false)}
                  items={methods.fields}
                  rawItems={rawMethods.fields}
                  projectCountry={projectCountry}
                  projectTimeZone={projectTimeZone}
                  isImperial={isImperial}
                  dataView={dataView}
                  setDataView={setDataView}
                  openAll={openAllItems}
                  enrichedDisabled={enrichedDisabled}
                />
              )}
              {dataView === RAW && (
                <AddNewItem
                  onClick={handleAddNewLineItem}
                  style={{ margin: '0.5rem 0' }}
                >
                  Add new item
                </AddNewItem>
              )}
              {dataView === ENRICHED && (
                <p style={{ margin: '0.5rem 0', minWidth: '5.375rem' }}>
                  &nbsp;
                </p>
              )}
            </TitleBar>
          </FormSectionWrapper>
          {methods.fields.map((item, i) => (
            <WasteLineItem
              key={item.hookFormId}
              trigger={`${item.hookFormId}-${reloadItemsCount}`}
              index={i}
              item={item}
              isImperial={isImperial}
              rawItem={
                rawMethods.fields && rawMethods.fields[i]
                  ? rawMethods.fields[i]
                  : {}
              }
              onDuplicate={duplicateLineItem}
              onDuplicateMultiple={duplicateLineItemMultiple}
              onRemove={lineItemRemoveHandlers[i]}
              setLineItemErrors={handleSetLineItemErrors}
              dataView={dataView}
              showRaw={showRaw}
              saving={saving}
              setEnrichedDisabled={value => {
                setEnrichedDisabled(value)
              }}
            />
          ))}

          {dataView === RAW && (
            <Button
              onClick={handleAddNewLineItem}
              block
              icon={<Icons.PlusCircleFilled />}
              type="dashed"
              htmlType="button"
            >
              Add new item
            </Button>
          )}
          <Button
            onClick={e => (document.getElementById('form-panel').scrollTop = 0)}
            style={{ marginTop: '1rem' }}
            className="backToTopButton"
          >
            Back to top
          </Button>
        </LineItemWrapper>
      </Form>

      <AttachmentView
        record={record}
        handleExtractionValueClicked={extractionValueClicked}
        currentStatus={currentStatus}
        projectLocale={projectLocale}
      />
      {record.history && (
        <RecordHistory
          changes={record.history.changes}
          moduleName={moduleName}
          isImperial={isImperial}
          projectLocale={projectLocale}
          projectTimeZone={projectTimeZone}
          lineItemCount={record.data.items.length}
          onCloseClick={onHistoryCloseClick}
          showHistory={showHistory}
          accountId={accountId}
          projectId={projectId}
          recordId={recordId}
        />
      )}

      <AddMultipleLineItemsModal
        moduleName={moduleName}
        isOpen={isAddMultipleLineItemsOpen}
        onClose={closeAddNewItemsModal}
        onSubmit={val => {
          addMultipleNewLineItems(val)
        }}
        duplicateLineItemName={null}
      />
    </SectionProvider>
  )
}

Waste.propTypes = {
  record: PropTypes.object,
  submit: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  acceptDataAfterSaveCount: PropTypes.number
}

function trackAndProduceRecord(record, formRecord, projectTimeZone) {
  return produce(_.merge({}, record, formRecord), draft => {
    draft.enriched.removalDate = isoDateFromFormDate(
      draft.enriched.removalDate,
      projectTimeZone
    )
    draft.enriched.removalExitDate = isoDateFromFormDate(
      draft.enriched.removalExitDate,
      projectTimeZone
    )
    draft.enriched.items = formRecord.enriched.items
    draft.assignee = formRecord.assignee
  })
}
